import { v4 as uuid } from 'uuid';

import { startDeadline } from '~/backend/requestDeadline';
import {
    ApprovedLockError,
    MissingPreconditionError,
    PersistenceRequestError,
    StaleVersionError,
} from '~/domain/persistence/PersistenceErrors';

/**
 * Shared HTTP plumbing for the hand-written dataset save clients
 * (structure sets, registrations, image/dose deletes).
 *
 * The generated client cannot make these calls: it has no multipart body
 * support, cannot send the `If-Match` header, and discards both the ETag
 * response header and the 409 body's `currentVersion` extension — all of
 * which the save contract depends on. Conventions mirror
 * `connector-next-fetcher.ts`: same-origin relative base URL, and only the
 * ProblemDetails `title` is ever read from an error body (`detail` may
 * carry PHI and is deliberately never parsed).
 */

const BASE_URL = '';

/**
 * The save attempt currently in flight, if any. One user "Save" action
 * fans out into 1–10 HTTP requests; tagging each with the same
 * client-minted id turns them back into ONE traceable clinical event in
 * the connector log (the log's per-request correlation ids are
 * unrelated to each other). Module-scoped rather than threaded through
 * the savers: saves are exclusive (PatientSaveService.inFlight), and no
 * non-save traffic flows through this module.
 */
let currentSaveAttemptId: string | null = null;

const SAVE_ATTEMPT_HEADER = 'X-Save-Attempt-Id';

/**
 * Runs one save attempt with a freshly minted attempt id: every request
 * {@link send} issues while `work` is pending carries the id. Cleared in
 * finally so an aborted save cannot leak its id onto later traffic.
 */
export async function runSaveAttempt<T>(work: () => Promise<T>): Promise<T> {
    currentSaveAttemptId = uuid();
    try {
        return await work();
    } finally {
        currentSaveAttemptId = null;
    }
}

/** Error-body fields the save clients are allowed to read. */
type ProblemShape = {
    title: string | null;
    /** ConcurrencyGuard's 409 extension carrying the row's live token. */
    currentVersion: string | null;
    /**
     * Whether the response was declared as ProblemDetails. Read from the
     * content type, NOT from whether a title parsed: the 409 stale-version
     * token must keep working even against a server that mislabels the body,
     * so parsing stays unconditional and this flag is advisory on top.
     *
     * It exists for one decision — an idempotent DELETE. A 404 the connector
     * declared is "the row is gone"; a 404 from a reverse proxy in front of it
     * is "your request never arrived", and those mean opposite things.
     */
    isProblemDetails: boolean;
};

async function readProblem(response: Response): Promise<ProblemShape> {
    const isProblemDetails = (
        response.headers.get('content-type') ?? ''
    ).includes('application/problem+json');
    try {
        const body: unknown = await response.json();
        if (typeof body !== 'object' || body === null) {
            return { title: null, currentVersion: null, isProblemDetails };
        }
        const record = body as Record<string, unknown>;
        return {
            title: typeof record.title === 'string' ? record.title : null,
            currentVersion:
                typeof record.currentVersion === 'string'
                    ? record.currentVersion
                    : null,
            isProblemDetails,
        };
    } catch {
        return { title: null, currentVersion: null, isProblemDetails };
    }
}

/** Maps a non-ok response to the typed persistence errors. Never returns. */
async function throwForResponse(response: Response): Promise<never> {
    const problem = await readProblem(response);
    if (response.status === 428) {
        throw new MissingPreconditionError();
    }
    if (response.status === 409) {
        // ConcurrencyGuard's stale-version 409 carries `currentVersion`;
        // the approved-lock 409 does not.
        if (problem.currentVersion !== null) {
            throw new StaleVersionError(problem.currentVersion);
        }
        throw new ApprovedLockError();
    }
    throw new PersistenceRequestError(
        response.status,
        problem.title,
        problem.isProblemDetails
    );
}

/**
 * Deadline for an ordinary save request. Generous — a busy connector on a
 * clinical network can be slow — but finite: without one, a half-open socket
 * (a dropped VPN, a proxy that blackholes) leaves the promise pending forever,
 * so `PatientSaveService.inFlight` never clears, the header spins for the rest
 * of the session, and every later save is refused with "A save is already in
 * progress". A hung request has to degrade to a per-entry failure the user can
 * retry, not a wedged session.
 */
const REQUEST_TIMEOUT_MS = 60_000;

/**
 * Deadline for a bulk upload. The derived-series upload can carry up to 512 MB
 * in one request, which on a slow link legitimately takes minutes.
 */
const UPLOAD_TIMEOUT_MS = 10 * 60_000;

const NULL_BODY_STATUSES = new Set([204, 205, 304]);

export async function send(
    path: string,
    init: RequestInit,
    ifMatch?: string
): Promise<Response> {
    const headers = new Headers(init.headers);
    if (ifMatch !== undefined) {
        headers.set('If-Match', `"${ifMatch}"`);
    }
    if (currentSaveAttemptId !== null) {
        headers.set(SAVE_ATTEMPT_HEADER, currentSaveAttemptId);
    }

    // Body-carrying uploads get the longer deadline.
    const timeoutMs =
        init.body instanceof FormData ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS;
    const deadline = startDeadline(timeoutMs, init.signal);

    try {
        const response = await fetch(`${BASE_URL}${path}`, {
            ...init,
            headers,
            signal: deadline.signal,
        });

        // The body is read HERE, inside the deadline. Resolving the headers is
        // not the same as completing the request: a socket that stalls after
        // the headers arrive would leave a later `.json()` hanging forever,
        // which is the exact wedge this deadline exists to prevent. Every
        // response on this path is small JSON or empty, so buffering it costs
        // nothing and makes the downstream reads memory-only.
        // 204/205/304 are null-body statuses: the Response constructor rejects
        // a body for them, even an empty one. A DELETE returning 204 is the
        // common case here.
        const body = NULL_BODY_STATUSES.has(response.status)
            ? null
            : await response.arrayBuffer();
        const buffered = new Response(body, {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
        });

        if (!buffered.ok) {
            await throwForResponse(buffered);
        }
        return buffered;
    } catch (error) {
        // throwForResponse's typed errors must pass through untouched; only a
        // transport failure maps to the generic request error. An abort is one
        // of those, and should be: both mean "this entry did not save, try
        // again".
        if (
            error instanceof MissingPreconditionError ||
            error instanceof StaleVersionError ||
            error instanceof ApprovedLockError ||
            error instanceof PersistenceRequestError
        ) {
            throw error;
        }
        throw new PersistenceRequestError(null, null);
    } finally {
        deadline.clear();
    }
}

/** Parses a JSON body, mapping a malformed one to a request error. */
export async function parseJsonBody<T>(response: Response): Promise<T> {
    try {
        return (await response.json()) as T;
    } catch {
        throw new PersistenceRequestError(response.status, null);
    }
}

/**
 * Reads a post-update version from the ETag response header. Used where a
 * response body carries no id/version — a 200 without a usable ETag leaves
 * the client without a token for the next If-Match, so it is treated as a
 * failed request rather than proceeding with stale bookkeeping.
 */
export function readVersionFromEtag(response: Response): string {
    const etag = response.headers.get('ETag');
    const version = etag === null ? '' : etag.replaceAll('"', '').trim();
    if (version === '') {
        throw new PersistenceRequestError(response.status, null);
    }
    return version;
}
